// Shared helpers for the GymTracker session UI: time formatting, default naming,
// and a client-side calorie estimate that mirrors the backend formula
// (caloriesᵢ = METᵢ × bodyWeightKg × allocatedHoursᵢ).

export function pad2(n) { return String(n).padStart(2, '0'); }

// Normalise a `v-model.number` value before sending it to GraphQL. Empty number
// inputs come through as "" (not null), and GraphQL Int/Float coercion rejects ""
// with "cannot deserialize the given value" → HTTP 500. Map ""/undefined/NaN → null.
export function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isNaN(n) ? null : n;
}

// Seconds → "m:ss" or "h:mm:ss"
export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${m}:${pad2(sec)}`;
}

// Seconds → compact human label, e.g. "1 h 5 min" / "45 min" / "30 s"
export function formatDurationLong(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  return `${m} min`;
}

const TIME_OF_DAY = [
  { until: 5,  name: 'Nattpass' },
  { until: 9,  name: 'Morgonpass' },
  { until: 11, name: 'Förmiddagspass' },
  { until: 13, name: 'Lunchpass' },
  { until: 17, name: 'Eftermiddagspass' },
  { until: 22, name: 'Kvällspass' },
  { until: 24, name: 'Nattpass' },
];

export function defaultSessionName(date = new Date()) {
  const h = date.getHours();
  return (TIME_OF_DAY.find(t => h < t.until) || TIME_OF_DAY[TIME_OF_DAY.length - 1]).name;
}

// Sum of all measured set durations — the minimum the session can have lasted.
export function minDurationSeconds(exercises) {
  return (exercises || []).reduce((sum, ex) =>
    sum + (ex.sets || []).reduce((s, set) => s + (set.durationSeconds || 0), 0), 0);
}

// Client-side calorie estimate. allocations is a map { [exerciseId]: secondsOrNull }.
// Exercises with a non-null allocation keep that time (floored to their measured
// set time); remaining time is split evenly across the rest. Returns
// { total, perExercise: { [id]: { seconds, calories } } }.
export function computeCalories(exercises, bodyWeightKg, totalSeconds, allocations = {}) {
  const result = { total: 0, perExercise: {} };
  const list = exercises || [];
  if (list.length === 0 || !bodyWeightKg) {
    list.forEach(ex => { result.perExercise[ex.id] = { seconds: 0, calories: 0 }; });
    return result;
  }

  const floor = {};
  list.forEach(ex => {
    floor[ex.id] = (ex.sets || []).reduce((s, set) => s + (set.durationSeconds || 0), 0);
  });

  const explicit = list.filter(ex => allocations[ex.id] != null);
  const auto = list.filter(ex => allocations[ex.id] == null);

  const assigned = {};
  let usedByExplicit = 0;
  explicit.forEach(ex => {
    const sec = Math.max(allocations[ex.id], floor[ex.id]);
    assigned[ex.id] = sec;
    usedByExplicit += sec;
  });

  const autoFloors = auto.reduce((s, ex) => s + floor[ex.id], 0);
  const leftover = Math.max(0, (totalSeconds || 0) - usedByExplicit - autoFloors);
  const perAuto = auto.length > 0 ? leftover / auto.length : 0;
  auto.forEach(ex => { assigned[ex.id] = Math.round(floor[ex.id] + perAuto); });

  list.forEach(ex => {
    const sec = assigned[ex.id] || 0;
    const met = ex.exerciseType?.metValue ?? 5.0;
    const cals = met * bodyWeightKg * (sec / 3600);
    result.perExercise[ex.id] = { seconds: sec, calories: cals };
    result.total += cals;
  });
  return result;
}

// One-line label for a logged set based on the exercise's capability flags.
export function setSummary(set, et) {
  if (!set) return '';
  const parts = [];
  const t = et || {};
  if (t.tracksWeight && set.weight != null && set.weight !== 0 && t.tracksReps && set.reps != null) {
    parts.push(`${set.weight} kg × ${set.reps}`);
  } else {
    if (t.tracksReps && set.reps != null) parts.push(`${set.reps} reps`);
    if (t.tracksWeight && set.weight != null && set.weight !== 0) parts.push(`${set.weight} kg`);
  }
  if (t.tracksDistance && set.distanceMeters != null) parts.push(`${set.distanceMeters} m`);
  if (t.tracksDuration && set.durationSeconds != null) parts.push(formatDuration(set.durationSeconds));
  if (set.rpe != null) parts.push(`RPE ${set.rpe}`);
  return parts.join(' · ') || '–';
}

// Estimated 1-rep max (Epley) for PR comparisons on weight×reps sets.
export function epley1rm(weight, reps) {
  if (!weight || !reps) return 0;
  return weight * (1 + reps / 30);
}
